import { page } from "$app/state";
import { browser } from "$app/environment";

import type { CheckoutData, ConfirmationData, Product, ProductVariant } from "@agapi/mlc-core";

import { type CartStore } from "../stores";

import { getCommerce } from '../services';
import { getCookie } from './cookies.client';
import { getLocaleFromUrl } from './locale.shared';
import { captureEvent } from './posthog/posthog.client';

/**
 * Sends a Loop54 click event for a product.
 */
const sendLoop54Click = async (param: Product, identifier: string) => {
  const variant = param.variants[0];
  const url = new URL(window.location.href);
  const COMMERCE = getCommerce(window.fetch);
  await COMMERCE.events([{ type: 'click', sku: variant.sku }], getLocaleFromUrl(url), { userId: identifier });
};

/**
 * Sends a Loop54 add to cart event.
 */
const sendLoop54AddToCart = async ({ variant, quantity }: { variant: ProductVariant, quantity: number }, identifier: string) => {
  const url = new URL(window.location.href);
  const COMMERCE = getCommerce(window.fetch);
  await COMMERCE.events([{ type: 'addToCart', sku: variant.sku, quantity }], getLocaleFromUrl(url), { userId: identifier });
};

/**
 * Sends a Loop54 purchase event.
 */
const sendLoop54Purchase = async (param: ConfirmationData, identifier: string) => {
  const url = new URL(window.location.href);
  const COMMERCE = getCommerce(window.fetch);
  await COMMERCE.events(param.order.items.map((i) => ({
    type: 'purchase',
    sku: i.sku,
    name: i.name,
    quantity: i.quantity,
    orderId: param.orderId,
    revenue: param.order.totals.grandTotal
  })),
  getLocaleFromUrl(url),
  { userId: identifier });
};

/**
 * Sends a Google Tag Manager (GTM) view item event.
 */
const sendGTMViewItem = (param: Product, identifier: string, GTM: unknown[]) => {
  const variant = param.variants[0];

  GTM.push({
    event: "view_item",
    ecommerce: {
      user_email: identifier,
      currency: variant.price.currency,
      value: variant.price.amount / 100,
      items: [{
        item_id: variant.sku,
        item_name: variant.name,
        item_brand: variant.brand.name,
        item_category: variant.categories?.[0],
        price: variant.price.amount / 100,
        quantity: 1
      }]
    }
  });
};

/**
 * Sends a PostHog view item event
 */
const sendPostHogViewItem = (param: Product) => {
  const variant = param.variants[0];

  captureEvent('viewed_product', {
    product_id: variant.sku,
    product_name: variant.name,
    product_brand: variant.brand.name,
    product_category: variant.categories?.[0],
    price: variant.price.amount / 100,
    currency: variant.price.currency
  });
};

/**
 * Sends a Google Tag Manager (GTM) add to cart event.
 */
const sendGTMAddToCart = ({ variant, quantity }: { variant: ProductVariant, quantity: number }, identifier: string, GTM: unknown[]) => {
  GTM.push({
    event: "add_to_cart",
    ecommerce: {
      user_email: identifier,
      currency: variant.price.currency,
      value: variant.price.amount / 100,
      items: [{
        item_id: variant.sku,
        item_name: variant.name,
        item_brand: variant.brand.name,
        item_category: variant.categories?.[0],
        price: variant.price.amount / 100,
        quantity: quantity
      }]
    }
  });
};

/**
 * Sends a PostHog add to cart event
 */
const sendPostHogAddToCart = ({ variant, quantity }: { variant: ProductVariant, quantity: number }) => {
  captureEvent('added_to_cart', {
    product_id: variant.sku,
    product_name: variant.name,
    product_brand: variant.brand.name,
    product_category: variant.categories?.[0],
    price: variant.price.amount / 100,
    currency: variant.price.currency,
    quantity: quantity,
    value: (variant.price.amount / 100) * quantity
  });
};

/**
 * Sends a Google Tag Manager (GTM) remove from cart event.
 */
export const sendGTMRemoveFromCart = ({ variant, quantity }: { variant: ProductVariant, quantity: number }, identifier: string, GTM: unknown[]) => {
  GTM.push({
    event: "remove_from_cart",
    ecommerce: {
      user_email: identifier,
      currency: variant.price.currency,
      value: variant.price.amount / 100,
      items: [{
        item_id: variant.sku,
        item_name: variant.name,
        item_brand: variant.brand.name,
        item_category: variant.categories?.[0],
        price: variant.price.amount / 100,
        quantity: quantity
      }]
    }
  });
};

/**
 * Sends a PostHog remove from cart event
 */
const sendPostHogRemoveFromCart = ({ variant, quantity }: { variant: ProductVariant, quantity: number }) => {
  captureEvent('removed_from_cart', {
    product_id: variant.sku,
    product_name: variant.name,
    product_brand: variant.brand.name,
    product_category: variant.categories?.[0],
    price: variant.price.amount / 100,
    currency: variant.price.currency,
    quantity: quantity,
    value: (variant.price.amount / 100) * quantity
  });
};

/**
 * Sends a Google Tag Manager (GTM) view cart event.
 */
const sendGTMViewCart = (identifier: string, GTM: unknown[], cart?: CartStore) => {
  GTM.push({
    event: "view_cart",
    ecommerce: {
      user_email: identifier,
      currency: 'SE', // cartStore.currencyCode,
      value: (cart?.summary.raw.totalAmount ?? 0) / 100,
      items: cart?.items.map((i) => ({
        item_id: i.sku,
        item_name: i.name,
        item_brand: i.brand,
        item_category: i.category,
        price: i.price.amount / 100,
        quantity: 1
      }))
    }
  });
};

/**
 * Sends a PostHog view cart event
 */
const sendPostHogViewCart = (cart?: CartStore) => {
  if (!cart) return;

  captureEvent('viewed_cart', {
    currency: 'SEK',
    value: (cart.summary.raw.totalAmount ?? 0) / 100,
    items: cart.items.map(i => ({
      product_id: i.sku,
      product_name: i.name,
      product_brand: i.brand,
      product_category: i.category,
      price: i.price.amount / 100,
      quantity: i.quantity
    }))
  });
};

/**
 * Sends a Google Tag Manager (GTM) checkout begin event.
 */
const sendGTMCheckoutBegin = (param: CheckoutData, identifier: string, GTM: unknown[]) => {
  const event = {
    event: "checkout_begin",
    ecommerce: {
      user_email: identifier,
      currency: param?.currencyCode ?? 'SEK',
      value: (param?.totals.grandTotal ?? 0) / 100,
      items: param?.items.map((i) => ({
        item_id: i.sku,
        item_name: i.name,
        price: i.salePriceAmount / 100,
        quantity: i.quantity
      }))
    }
  };

  console.log('[GTM] Exists?', GTM);
  console.log('[GTM] Checkout begin', event);
  GTM.push(event);
};

/**
 * Sends a PostHog checkout begin event
 */
const sendPostHogCheckoutBegin = (param: CheckoutData) => {
  captureEvent('began_checkout', {
    currency: param?.currencyCode ?? 'SEK',
    value: (param?.totals.grandTotal ?? 0) / 100,
    items: param?.items.map(i => ({
      product_id: i.sku,
      product_name: i.name,
      price: i.salePriceAmount / 100,
      quantity: i.quantity
    }))
  });
};

/**
 * Sends a Google Tag Manager (GTM) purchase event.
 */
const sendGTMPurchase = (param: ConfirmationData, identifier: string, GTM: unknown[]) => {
  console.log('sendGTMPurchase', param);

  getCommerce(window.fetch).getProductVariant(param.order.items[0].sku, getLocaleFromUrl(page.url), {
    userId: getCookie<string>('__agapi_identifier') ?? 'default'
  }).then((variant) => {
    console.log('[GTM] Purchase', {
      event: 'purchase',
      email: param.billingAddress.email,
      ecommerce: {
        user_email: identifier,
        transaction_id: param.order.reference,
        currency: 'SEK',
        value: param.order.totals.grandTotal / 100,
        items: param.order.items.map((i) => ({
          item_id: i.sku,
          item_name: i.name,
          price: i.salePriceAmount / 100,
          quantity: i.quantity
        }))
      }
    }, GTM);

    GTM.push({
      event: 'purchase',
      email: param.billingAddress.email,
      ecommerce: {
        user_email: identifier,
        transaction_id: param.order.reference,
        currency: 'SEK',
        value: param.order.totals.grandTotal / 100,
        items: param.order.items.map((i) => ({
          item_id: i.sku,
          item_name: i.name,
          item_brand: variant.item?.brand.name,
          item_category2: i.sku,
          price: i.salePriceAmount / 100,
          quantity: i.quantity
        }))
      }
    });

    GTM.push({
      event: 'profitmetrics',
    })
  });
};

/**
 * Sends a PostHog purchase event
 */
const sendPostHogPurchase = (param: ConfirmationData) => {
  captureEvent('completed_order', {
    order_id: param.orderId,
    currency: 'SEK',
    value: param.order.totals.grandTotal / 100,
    revenue: param.order.totals.grandTotal / 100,
    shipping: param.order.totals.shippingTotal / 100,
    tax: param.order.totals.taxTotal / 100,
    items: param.order.items.map(i => ({
      product_id: i.sku,
      product_name: i.name,
      price: i.salePriceAmount / 100,
      quantity: i.quantity
    })),
    email: param.billingAddress.email,
    customer: {
      first_name: param.billingAddress.givenName,
      last_name: param.billingAddress.familyName,
      email: param.billingAddress.email
    },
    billing_address: {
      first_name: param.billingAddress.givenName,
      last_name: param.billingAddress.familyName,
      address: param.billingAddress.streetAddress,
      city: param.billingAddress.city,
      postal_code: param.billingAddress.postalCode,
      country: param.billingAddress.country
    },
    shipping_address: {
      first_name: param.shippingAddress.givenName,
      last_name: param.shippingAddress.familyName,
      address: param.shippingAddress.streetAddress,
      city: param.shippingAddress.city,
      postal_code: param.shippingAddress.postalCode,
      country: param.shippingAddress.country
    }
  });
};

/**
 * Emits client-side events to various tracking services.
 */
export const clientEventEmitter = async function (event: string, param: unknown, cart?: CartStore) {
  const identifier = getCookie<string>('__agapi_identifier');
  if (identifier) {
    const GTM = (window as typeof window & { dataLayer: unknown[] }).dataLayer;

    if (browser) {
      switch (event) {
        case 'product_view': {
          const product = param as Product;
          sendGTMViewItem(product, identifier, GTM);
          sendPostHogViewItem(product);
          sendLoop54Click(product, identifier);
          break;
        }
        case 'cart_item_add': {
          const variantQuantity = param as { variant: ProductVariant, quantity: number };
          sendGTMAddToCart(variantQuantity, identifier, GTM);
          sendPostHogAddToCart(variantQuantity);
          sendLoop54AddToCart(variantQuantity, identifier);
          break;
        }
        case 'cart_item_remove': {
          const variantQuantity = param as { variant: ProductVariant, quantity: number };
          sendGTMRemoveFromCart(variantQuantity, identifier, GTM);
          sendPostHogRemoveFromCart(variantQuantity);
          break;
        }
        case 'cart_view': {
          sendGTMViewCart(identifier, GTM, cart);
          sendPostHogViewCart(cart);
          break;
        }
        case 'checkout_view': {
          const checkout = param as CheckoutData;
          sendGTMCheckoutBegin(checkout, identifier, GTM);
          sendPostHogCheckoutBegin(checkout);
          break;
        }
        case 'thankyou_view': {
          const confirmation = param as ConfirmationData;
          sendGTMPurchase(confirmation, identifier, GTM);
          sendPostHogPurchase(confirmation);
          sendLoop54Purchase(confirmation, identifier);
          break;
        }
      }
    }
  }
};
